import { Alert, Button, Checkbox, Form, Input, message, Modal, Select } from "antd";
import { FormWizard } from "components/formWizard";
import { formatErrorMessage } from "common/redux/middleware/queryErrorLogger";
import { formatCurrency } from "common/utils";
import { getPaymentIntents } from "features/payments/paymentDefaults";
import {
  useCalculateConvenienceFeesMutation,
  useCreatePaymentIntentsMutation,
  useGetPaymentMethodOptionsQuery,
} from "features/payments/paymentsAPI";
import { selectPaymentsSlice } from "features/payments/paymentsSlice";
import { useSelector } from "react-redux";
import moment from "moment-timezone";

import { useUserType } from "features/auth";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import currency from "currency.js";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import { useEffect, useMemo, useState } from "react";
import { DATE_FORMAT } from "common/constants";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

function SelectExistingPaymentMethodForm({ onOk, onCancel }) {
  const { isAgencyUserType } = useUserType();
  const { debtorId } = useParams();
  const [form] = Form.useForm();
  const [currentConvenienceFee, setCurrentConvenienceFee] = useState(null);
  const [convenienceFeeError, setConvenienceFeeError] = useState(null);
  const [showDisclosureWizard, setShowDisclosureWizard] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const { data: agencySettings } = useFetchAgencyQuery();
  const { data: isCanWaiveConvenienceFeeAuthorized } = useAuthorization(
    PERMISSIONS.PAYMENT__WAIVE_CONVENIENCE_FEES,
  );
  const [fetchConvenienceFees] = useCalculateConvenienceFeesMutation();

  const [createPaymentIntents, { isLoading: confirmPaymentLoading }] =
    useCreatePaymentIntentsMutation();
  const { data: paymentOptions, isLoading: isPaymentMethodOptionsLoading } =
    useGetPaymentMethodOptionsQuery(
      {
        debtorId,
      },
      { skip: !isAgencyUserType },
    );
  const paymentsSlice = useSelector(selectPaymentsSlice);

  useEffect(() => {
    (async () => {
      const value = form.getFieldValue("existingPaymentMethodOptionId");
      const paymentMethod = paymentOptions?.find((pm) => pm.id === value);
      const isCardFeeEnabled =
        paymentMethod?.type === "process_card" &&
        agencySettings?.isConvenienceFeeCardPaymentsEnabled;
      const isAchFeeEnabled =
        paymentMethod?.type === "process_ach" && agencySettings?.isConvenienceFeeAchPaymentsEnabled;

      if (isCardFeeEnabled || isAchFeeEnabled) {
        if (!paymentMethod?.state || !paymentMethod?.zipCode) {
          setCurrentConvenienceFee(null);
          setConvenienceFeeError(
            "State or zip code is missing on this payment method. Please use a different payment method or create a new one.",
          );
          return;
        }
        const result = await fetchConvenienceFees({
          accountIds: paymentsSlice.selectedAccounts,
          amounts: [paymentsSlice.paymentsSchedule[0].totalAmount],
          state: paymentMethod?.state,
          zipCode: paymentMethod?.zipCode,
          paymentMethodType: paymentMethod?.type,
        });
        if ("data" in result) {
          setCurrentConvenienceFee(result.data.convenienceFees[0]);
          setConvenienceFeeError(null);
        } else {
          setConvenienceFeeError(formatErrorMessage(result.error));
        }
      } else {
        setCurrentConvenienceFee({ message: null, fee: 0, amount: 0 });
        setConvenienceFeeError(null);
      }
    })();
  }, [
    fetchConvenienceFees,
    paymentsSlice.selectedAccounts,
    form,
    paymentsSlice.totalAmount,
    paymentsSlice.paymentsSchedule,
    paymentOptions,
    agencySettings?.isConvenienceFeeCardPaymentsEnabled,
    agencySettings?.isConvenienceFeeAchPaymentsEnabled,
  ]);

  const handleDisclosureWizardDone = async () => {
    if (!pendingAction) return;

    const result = await pendingAction();
    if ("data" in result) {
      setShowDisclosureWizard(false);
      setPendingAction(null);
      setPendingValues(null);
      return onOk();
    }
    if ("error" in result) {
      message.error("Failed to process the payment. Please refresh and try again.");
      setShowDisclosureWizard(false);
      setPendingAction(null);
      setPendingValues(null);
    }
  };

  const handleDisclosureWizardCancel = () => {
    setShowDisclosureWizard(false);
    setPendingAction(null);
    setPendingValues(null);
  };

  const disclosureSteps = useMemo(() => {
    const steps = [];
    const waiveConvenienceFees = form.getFieldValue("waiveConvenienceFees");

    if (currency(currentConvenienceFee?.fee).value > 0 && !waiveConvenienceFees) {
      steps.push({
        title: "Convenience Fee Disclosure",
        doneButtonText: "Pay",
        form: (
          <div>
            <p>
              Attention:{" "}
              {currentConvenienceFee?.message ||
                "You are authorizing the payment service provider to charge your payment method for the principal amount and a convenience fee. For ACH payments, this will appear on your statement as two separate transactions. For card payments, this will appear as one transaction. You understand that you are paying a fee for the convenience of making this payment by phone and that you can avoid this fee by mailing a check or money order to our office."}
            </p>
            <p>
              <strong>Convenience Fee: {formatCurrency(currentConvenienceFee?.fee)}</strong>
            </p>
            <Form.Item
              name="convenienceFeeAcknowledged"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("You must acknowledge the convenience fee disclosure"),
                        ),
                },
              ]}
            >
              <Checkbox>I understand and agree to the convenience fee disclosure above.</Checkbox>
            </Form.Item>
          </div>
        ),
      });
    }

    if (agencySettings?.paymentDisclosureNotice) {
      steps.push({
        title: "Payment Disclosure",
        doneButtonText: "Pay",
        form: (
          <div>
            <p>{agencySettings?.paymentDisclosureNotice}</p>
            <Form.Item
              name="paymentAcknowledged"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error("You must acknowledge the payment disclosure")),
                },
              ]}
            >
              <Checkbox>I understand and agree to the payment disclosure above.</Checkbox>
            </Form.Item>
          </div>
        ),
      });
    }
    return steps;
  }, [
    currentConvenienceFee,
    form,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    form.getFieldValue("waiveConvenienceFees"),
    agencySettings?.paymentDisclosureNotice,
  ]);

  const onExistingPaymentMethodFinish = async (values) => {
    const { existingPaymentMethodOptionId, notes } = values;

    const action = async () => {
      try {
        const intents = getPaymentIntents({
          paymentMethodId: existingPaymentMethodOptionId,
          paymentsSchedule: paymentsSlice.paymentsSchedule,
          isPaymentPlan: paymentsSlice.isPaymentPlan,
        });
        const result = await createPaymentIntents({
          paymentMethodId: existingPaymentMethodOptionId,
          paymentIntentType: paymentsSlice.paymentIntentType,
          debtorId,
          intents: intents.map((intent) => ({
            ...intent,
            notes,
            accountToPreallocationBreakdownMap: paymentsSlice.preallocationBreakdown,
            applyConvenienceFees: !values.waiveConvenienceFees,
          })),
        });
        return result;
      } catch (e) {
        message.error("Failed to process the payment. Please refresh and try again.");
        throw e;
      }
    };

    const selectedPaymentMethod = paymentOptions?.find(
      (pm) => pm.id === existingPaymentMethodOptionId,
    );
    const isProcessPaymentMethod =
      selectedPaymentMethod?.type === "process_ach" ||
      selectedPaymentMethod?.type === "process_card";
    const shouldShowDisclosure =
      (currency(currentConvenienceFee?.fee).value > 0 && !values.waiveConvenienceFees) ||
      (isProcessPaymentMethod && agencySettings?.paymentDisclosureNotice);

    if (shouldShowDisclosure) {
      setPendingAction(() => action);
      setPendingValues({
        notes,
        waiveConvenienceFees: values.waiveConvenienceFees,
      });
      setShowDisclosureWizard(true);
    } else {
      const result = await action();
      if ("error" in result) {
        return;
      }
      if ("data" in result) {
        onOk();
      }
    }
  };

  const getButtonText = (applyConvenienceFees) => {
    // For one-time payments
    if (!paymentsSlice.isPaymentPlan) {
      const amount = currency(paymentsSlice.totalAmount);
      if (applyConvenienceFees) {
        return `Confirm Payment (${formatCurrency(amount.add(currentConvenienceFee?.fee))})`;
      }
      return `Confirm Payment (${formatCurrency(amount)})`;
    }

    // For payment planss with down payments
    if (paymentsSlice.downPaymentAmount > 0) {
      // if there's a fee, we need to add it to the down payment
      const totalAmount = currency(paymentsSlice.downPaymentAmount);
      if (applyConvenienceFees) {
        return `Confirm Payment (${formatCurrency(
          totalAmount.add(currency(currentConvenienceFee?.fee)),
        )})`;
      }

      return `Confirm Payment (${formatCurrency(totalAmount)})`;
    }

    // For payment plans without a down payment (no payment today), it doesn't matter to show if there's a fee or not
    // unless there's a payment today.
    if (paymentsSlice.paymentsSchedule[0].scheduledDate === moment().format(DATE_FORMAT)) {
      const totalAmount = currency(paymentsSlice.paymentsSchedule[0].totalAmount);
      if (applyConvenienceFees) {
        return `Confirm Payment (${formatCurrency(
          currency(totalAmount).add(currency(currentConvenienceFee?.fee)),
        )})`;
      }
      return `Confirm Payment (${formatCurrency(totalAmount)})`;
    }

    return "Setup Payment Plan";
  };

  return (
    <>
      <StyledForm
        form={form}
        layout="vertical"
        validateMessages={{ required: "This is a required field" }}
        onFinish={onExistingPaymentMethodFinish}
      >
        <Form.Item
          name="existingPaymentMethodOptionId"
          label="Payment Method:"
          initialValue={
            paymentOptions?.length > 0 ? paymentOptions[paymentOptions.length - 1].id : ""
          }
          rules={[{ required: true, message: "Payment Method is required" }]}
        >
          <Select
            loading={isPaymentMethodOptionsLoading}
            options={paymentOptions?.map((paymentMethod) => ({
              value: paymentMethod.id,
              label:
                paymentMethod.type === "process_card"
                  ? `${paymentMethod.firstName} ${paymentMethod.lastName} - card ending in ${paymentMethod.cardLast4Digits}`
                  : `${paymentMethod.bankAccountName} - ACH ending in ${paymentMethod.bankAccountLast4Digits}`,
            }))}
          />
        </Form.Item>

        <Form.Item name="notes" label="Payment Notes">
          <Input.TextArea rows={4} />
        </Form.Item>

        {form.getFieldValue("existingPaymentMethodOptionId") !== null &&
        isCanWaiveConvenienceFeeAuthorized ? (
          <Form.Item valuePropName="checked" name="waiveConvenienceFees" label="">
            <Checkbox>
              Waive convenience fee of {formatCurrency(currentConvenienceFee?.fee)}
            </Checkbox>
          </Form.Item>
        ) : (
          <Form.Item>
            <i>
              A convenience fee of {formatCurrency(currentConvenienceFee?.fee)} will be applied.
            </i>
          </Form.Item>
        )}

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.waiveConvenienceFees !== currentValues.waiveConvenienceFees ||
            prevValues.existingPaymentMethodOptionId !== currentValues.existingPaymentMethodOptionId
          }
        >
          {({ getFieldValue }) => {
            const waiveConvenienceFees = getFieldValue("waiveConvenienceFees");
            const applyConvenienceFees = !waiveConvenienceFees;
            const selectedId = getFieldValue("existingPaymentMethodOptionId");
            const selectedMethod = paymentOptions?.find((pm) => pm.id === selectedId);
            const isConvenienceFeeEnabled =
              (selectedMethod?.type === "process_card" &&
                agencySettings?.isConvenienceFeeCardPaymentsEnabled) ||
              (selectedMethod?.type === "process_ach" &&
                agencySettings?.isConvenienceFeeAchPaymentsEnabled);
            const isFeeNotReady =
              isConvenienceFeeEnabled &&
              !waiveConvenienceFees &&
              (!!convenienceFeeError || currentConvenienceFee === null);

            return (
              <Form.Item>
                {convenienceFeeError && (
                  <Alert
                    type="error"
                    showIcon
                    message={`Can't calculate fee. ${convenienceFeeError}`}
                    style={{ marginBottom: 8 }}
                  />
                )}
                <Button disabled={confirmPaymentLoading} onClick={onCancel}>
                  Back
                </Button>
                <Button
                  disabled={confirmPaymentLoading || isFeeNotReady}
                  loading={confirmPaymentLoading}
                  style={{ marginLeft: 8 }}
                  type="primary"
                  htmlType="submit"
                >
                  {getButtonText(applyConvenienceFees)}
                </Button>
              </Form.Item>
            );
          }}
        </Form.Item>
      </StyledForm>
      {showDisclosureWizard && disclosureSteps?.length > 0 && (
        <Modal
          maskClosable={false}
          title="Acknowledgement and Agreement of Disclosures"
          open={showDisclosureWizard}
          onCancel={handleDisclosureWizardCancel}
          footer={null}
          width={700}
          closable
        >
          <FormWizard
            steps={disclosureSteps}
            onCancel={handleDisclosureWizardCancel}
            onDone={handleDisclosureWizardDone}
            initialValues={{
              convenienceFeeAcknowledged: false,
              paymentAcknowledged: false,
            }}
          />
        </Modal>
      )}
    </>
  );
}

export default SelectExistingPaymentMethodForm;
